import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";
import { eq } from "drizzle-orm";
import { ArrowLeft, BarChart3, CalendarClock, Megaphone, Monitor, Trophy } from "lucide-react";
import { useCallback, useRef, useState } from "react";
import { getDb } from "@/db";
import { events } from "@/db/schema";
import { getLiveState, type LiveScene, updateLiveState } from "@/lib/live-state";

const getEvent = createServerFn({ method: "GET" })
	.inputValidator((eventId: string) => eventId)
	.handler(async ({ data: eventId }) => {
		const db = getDb();
		const event = await db
			.select()
			.from(events)
			.where(eq(events.id, Number(eventId)))
			.get();
		if (!event) throw new Error("Event not found");
		return event;
	});

export const Route = createFileRoute("/admin/events/$eventId/tv-control")({
	loader: ({ params }) =>
		Promise.all([
			getEvent({ data: params.eventId }),
			getLiveState({ data: params.eventId }),
		]).then(([event, live]) => ({ event, liveState: live })),
	component: TVControlPage,
});

function TVControlPage() {
	const params = Route.useParams();
	const initialData = Route.useLoaderData();
	const inputRef = useRef<HTMLInputElement>(null);
	const [sending, setSending] = useState(false);

	const { data: liveScene } = useQuery({
		queryKey: ["tv-control-live", params.eventId],
		queryFn: () => getLiveState({ data: params.eventId }),
		initialData: initialData.liveState,
		refetchInterval: 2000,
	});

	const event = initialData.event;
	const currentScene = liveScene?.scene ?? "upcoming";

	const pushScene = useCallback(
		async (scene: LiveScene) => {
			setSending(true);
			try {
				await updateLiveState({ data: { eventId: event.id, scene } });
			} catch (err) {
				console.error("Failed to update scene:", err);
			} finally {
				setSending(false);
			}
		},
		[event.id],
	);

	const scenes: Array<{
		scene: string;
		label: string;
		icon: React.ReactNode;
		action: () => void;
	}> = [
		{
			scene: "upcoming",
			label: "Upcoming Heats",
			icon: <CalendarClock size={24} />,
			action: () => pushScene({ scene: "upcoming" }),
		},
		{
			scene: "awards",
			label: "Awards",
			icon: <Trophy size={24} />,
			action: () => pushScene({ scene: "awards" }),
		},
	];

	return (
		<div className="min-h-screen bg-background p-4 sm:p-6">
			<div className="max-w-md mx-auto">
				<Link
					to="/admin/events/$eventId"
					params={{ eventId: String(event.id) }}
					className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground text-sm mb-4 transition-colors"
				>
					<ArrowLeft size={16} />
					Back to {event.name}
				</Link>

				<div className="flex items-center gap-2 mb-6">
					<Monitor size={24} className="text-primary" />
					<h1 className="text-2xl text-foreground font-bold">TV Control</h1>
				</div>

				<div className="bg-card rounded-xl p-4 mb-4">
					<p className="text-xs text-muted-foreground uppercase font-bold mb-1">
						Current Scene
					</p>
					<p className="text-foreground font-bold text-lg capitalize">
						{currentScene}
					</p>
				</div>

				<div className="grid grid-cols-2 gap-3 mb-6">
					{scenes.map((s) => (
						<button
							key={s.scene}
							type="button"
							onClick={s.action}
							disabled={sending}
							className={`flex flex-col items-center gap-2 p-6 rounded-xl font-bold transition-all ${
								currentScene === s.scene
									? "bg-primary text-primary-foreground ring-2 ring-primary/50"
									: "bg-card hover:bg-muted text-foreground"
							}`}
						>
							{s.icon}
							<span className="text-sm">{s.label}</span>
						</button>
					))}
				</div>

				<div className="mb-6">
					<button
						type="button"
						onClick={() => pushScene({ scene: "leaderboard" })}
						disabled={sending}
						className={`w-full flex items-center justify-center gap-2 p-4 rounded-xl font-bold transition-all ${
							currentScene === "leaderboard"
								? "bg-gold text-primary-foreground ring-2 ring-gold/50"
								: "bg-card border-2 border-gold/40 text-gold hover:bg-gold/10"
						}`}
					>
						<BarChart3 size={20} />
						<span>Reveal Rankings</span>
					</button>
				</div>

				<div className="bg-card rounded-xl p-4">
					<h3 className="text-sm font-bold text-muted-foreground uppercase mb-3">
						<Megaphone size={14} className="inline mr-1" />
						Announcement
					</h3>
					<div className="flex gap-2">
						<input
							ref={inputRef}
							type="text"
							placeholder="Message to display..."
							className="flex-1 px-3 py-2 text-sm"
							onKeyDown={(e) => {
								if (e.key === "Enter") {
									const val = e.currentTarget.value.trim();
									if (val) {
										pushScene({
											scene: "announcement",
											message: val,
										});
										e.currentTarget.value = "";
									}
								}
							}}
						/>
						<button
							type="button"
							onClick={() => {
								const val = inputRef.current?.value.trim();
								if (val) {
									pushScene({
										scene: "announcement",
										message: val,
									});
									if (inputRef.current) inputRef.current.value = "";
								}
							}}
							disabled={sending}
							className="px-3 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-bold"
						>
							Send
						</button>
					</div>
				</div>

				<div className="mt-4 space-y-2">
					<Link
						to="/events/$eventId/tv"
						params={{ eventId: String(event.id) }}
						target="_blank"
						className="block text-center text-sm text-muted-foreground hover:text-foreground transition-colors"
					>
						Open TV Display →
					</Link>
					<p className="text-center text-xs text-muted-foreground">
						Open on the display screen. This page controls what it shows.
					</p>
				</div>
			</div>
		</div>
	);
}
